/**
 * Fees, taxes and extras — everything charged on top of the accommodation.
 *
 * ---------------------------------------------------------------------------
 * WHY THESE ARE NOT IN THE SEARCH
 * ---------------------------------------------------------------------------
 *
 * The tiling engine finds the cheapest way to COVER the nights. A tourist tax
 * is not a way of covering a night; it is simply owed. Letting it into the
 * search would mean the engine could legitimately "choose" the cheapest option
 * that avoids charging it.
 *
 * So fees are applied to the winning tiling afterwards. That also leaves every
 * guarantee about the search untouched: with no fees declared, a quote is
 * bit-identical to what it was before this file existed, which is what keeps
 * the differential against the legacy calculator meaningful.
 *
 * ---------------------------------------------------------------------------
 * ONE SHAPE FOR EVERYTHING
 * ---------------------------------------------------------------------------
 *
 * A fee is an amount and a BASIS. Naming the basis rather than enumerating fee
 * types is what lets one model carry every case the park actually has:
 *
 *   extra person on a pitch   perPersonPerNight, beyond the included two
 *   the fifth in a trekkershut  perPersonPerNight, beyond the included four
 *   a dog                     perNight, counting the dog quantity
 *   bed linen                 perPerson, optional, paid up front
 *   cleaning                  perStay
 *   toeristenbelasting        perPersonPerNight, kind: tax
 *
 * A rental unit that charges for nobody extra simply declares no fee — and
 * because `included === max` there, nobody extra can be booked either.
 */

import type { DayRange, Money, PieceSet, Quantities, QuoteExtra } from './types';

const round2 = (n: number) => Math.round(n * 100) / 100;

/** People counted for occupancy, taken at the peak across the stay. */
export function peakOccupancy(
  qty: Quantities, counts: readonly string[] | undefined, nights: number,
): number {
  const keys = counts ?? Object.keys(qty);
  let peak = 0;
  for (let d = 0; d < nights; d++) {
    let here = 0;
    for (const k of keys) here += qty[k]?.[d] ?? 0;
    if (here > peak) peak = here;
  }
  return peak;
}

/** Peak count of one quantity across the stay. */
function peakOf(qty: Quantities, key: string, nights: number): number {
  const vec = qty[key];
  if (!vec) return 0;
  let peak = 0;
  for (let d = 0; d < nights; d++) if ((vec[d] ?? 0) > peak) peak = vec[d] ?? 0;
  return peak;
}

/** Nights of the stay that fall inside a window (all of them when unset). */
function nightsIn(window: DayRange | undefined, arrival: number, nights: number): number {
  if (!window) return nights;
  let count = 0;
  for (let d = 0; d < nights; d++) {
    const day = arrival + d;
    if (day >= window.start && day < window.end) count++;
  }
  return count;
}

export interface OccupancyProblem {
  code: 'too-many-people' | 'too-many-pets';
  message: string;
}

/**
 * Does this party fit?
 *
 * Checked before the search, not during it: a party that does not fit has no
 * cheapest tiling to find, and reporting "no combination of tariffs covers this
 * period" would send the operator looking at the rate card for a problem that
 * is nothing to do with it.
 */
export function checkOccupancy(
  set: PieceSet, qty: Quantities, nights: number,
): OccupancyProblem | null {
  const occ = set.occupancy;
  if (!occ) return null;

  const counts = occ.counts ?? Object.keys(qty).filter((k) => k !== occ.pets?.quantity);
  const people = peakOccupancy(qty, counts, nights);
  if (people > occ.max) {
    return {
      code: 'too-many-people',
      message: `this accommodation takes ${occ.max} people and the booking is for ${people}`,
    };
  }
  if (occ.pets) {
    const pets = peakOf(qty, occ.pets.quantity, nights);
    if (pets > occ.pets.max) {
      return {
        code: 'too-many-pets',
        message: occ.pets.max === 0
          ? 'no pets are allowed in this accommodation'
          : `at most ${occ.pets.max} pet${occ.pets.max === 1 ? '' : 's'} are allowed `
            + `and the booking is for ${pets}`,
      };
    }
  }
  return null;
}

/**
 * Work out what is owed on top of the accommodation.
 *
 * `beyondIncluded` is the interesting one: it charges only the people past
 * `occupancy.included`, which is how "two are included, the third costs extra"
 * becomes one fee rather than a rule hidden in the rate card.
 */
export function computeExtras(
  set: PieceSet, qty: Quantities, arrival: number, nights: number,
): { extras: QuoteExtra[]; total: Money } {
  const fees = set.fees ?? [];
  if (fees.length === 0) return { extras: [], total: 0 };

  const occ = set.occupancy;
  const defaultCounts = occ?.counts
    ?? Object.keys(qty).filter((k) => k !== occ?.pets?.quantity);

  const extras: QuoteExtra[] = [];
  let total = 0;

  for (const fee of fees) {
    const chargeableNights = nightsIn(fee.active, arrival, nights);
    if (chargeableNights === 0 && fee.basis !== 'perStay' && fee.basis !== 'perPerson') continue;
    // A per-stay or per-person fee with a window still needs the stay to touch
    // that window at all, or a cleaning charge would follow a booking that
    // never overlaps the season it belongs to.
    if (fee.active && nightsIn(fee.active, arrival, nights) === 0) continue;

    const headcount = fee.quantity
      ? peakOf(qty, fee.quantity, nights)
      : peakOccupancy(qty, defaultCounts, nights);
    const billable = fee.beyondIncluded
      ? Math.max(0, headcount - (occ?.included ?? 0))
      : headcount;

    let units: number;
    switch (fee.basis) {
      case 'perStay': units = 1; break;
      case 'perNight': units = fee.quantity ? billable * chargeableNights : chargeableNights; break;
      case 'perPerson': units = billable; break;
      case 'perPersonPerNight': units = billable * chargeableNights; break;
      default: units = 0;
    }
    if (units <= 0) continue;

    const line: QuoteExtra = {
      id: fee.id,
      label: fee.label,
      kind: fee.kind ?? 'fee',
      basis: fee.basis,
      amount: fee.amount,
      units,
      total: round2(fee.amount * units),
      optional: Boolean(fee.optional),
    };
    extras.push(line);
    // Optional extras are quoted so a booking screen can offer them, but they
    // are not owed until chosen, so they stay out of the total.
    if (!line.optional) total += line.total;
  }

  return { extras, total: round2(total) };
}

// ---------------------------------------------------------------------------
// Mapping a park's guest categories onto a channel's fixed one
// ---------------------------------------------------------------------------

/**
 * What a channel would call this party.
 *
 * A park counts guests however its price list needs to: babies, kleuters,
 * kinderen, jeugd, volwassenen, honden. Every OTA counts them exactly one way
 * — adults, children, and sometimes infants — and that rule is not negotiable,
 * so an availability feed has to be expressed in it.
 *
 * The mapping is many-to-one and lossy by design. Three priced age bands can
 * all be `child` to the channel; the park still charges each band its own
 * rate, because the price came out of the engine in the PARK's vocabulary and
 * only the occupancy key is translated. Nothing here touches money.
 *
 * Taken at the peak of the stay, matching how occupancy is checked: a party
 * that grows on day three is a party of that size as far as a channel and a
 * fire regulation are concerned.
 */
export function channelOccupancy(
  set: PieceSet, qty: Quantities, nights: number,
): { adults: number; children: number; infants: number } {
  const guests = set.guests;
  const out = { adults: 0, children: 0, infants: 0 };
  for (const key of Object.keys(qty)) {
    // An undeclared quantity is assumed to be an adult rather than dropped:
    // under-reporting a party to a channel is the expensive direction of the
    // mistake, and a legacy programme has no categories at all.
    const role = guests?.[key]?.channel ?? 'adult';
    if (role === 'other') continue;
    const peak = peakOf(qty, key, nights);
    if (role === 'adult') out.adults += peak;
    else if (role === 'child') out.children += peak;
    else out.infants += peak;
  }
  return out;
}

/**
 * The channel-facing occupancy key for a published cell, e.g. `2A1C`.
 *
 * Infants are carried separately rather than folded into children, because a
 * channel that does not distinguish them can add the two and a channel that
 * does cannot un-add them.
 */
export function occupancyKey(o: { adults: number; children: number; infants: number }): string {
  let key = `${o.adults}A`;
  if (o.children) key += `${o.children}C`;
  if (o.infants) key += `${o.infants}I`;
  return key;
}
